import { MapPin, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Seo } from "../components/Seo";
import { PageHero } from "../components/PageHero";
import { Reveal, Eyebrow } from "../components/Reveal";
import { ServiceMarquee } from "../components/ServiceMarquee";
import { FinalCta } from "../components/FinalCta";
import { SUBURBS, SUBURB_PAGES, BUSINESS } from "../content";

export const ServiceAreasPage = () => (
  <>
    <Seo
      title="Cleaning Services Across Melbourne's South East | Pure Bliss Clean"
      description="Pure Bliss Clean services Carrum Downs and surrounding Melbourne South East suburbs — Cranbourne, Frankston, Langwarrin, Berwick, Mornington and more. Get a free quote."
      path="/service-areas"
    />
    <PageHero
      eyebrow="Service Areas"
      titleLines={[`Cleaning Services Across`, "Melbourne's South East"]}
      intro={`Pure Bliss Clean is proudly local, servicing ${BUSINESS.baseSuburb} and surrounding ${BUSINESS.region} suburbs with reliable residential, end-of-lease, property management and commercial cleaning.`}
    />
    <ServiceMarquee items={SUBURBS} />

    <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
      <Reveal className="max-w-2xl">
        <Eyebrow>Where We Clean</Eyebrow>
        <h2 className="font-display text-3xl font-black text-brand-ink sm:text-4xl">Local Cleaners, Local Knowledge</h2>
        <p className="mt-5 text-base leading-relaxed text-slate-600">
          From family homes in Carrum Downs and Cranbourne to rentals in Frankston and businesses in Berwick, we
          clean right across the South East. These are the areas we service most — if you're nearby but don't see
          your suburb, get in touch. Surrounding areas are often fine too.
        </p>
      </Reveal>
      <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {SUBURBS.map((s, i) => {
          const page = SUBURB_PAGES.find((p) => p.suburb === s);
          const card = (
            <div
              data-testid={`suburb-card-${s.toLowerCase().replace(/\s+/g, "-")}`}
              className="group flex h-full items-center gap-3.5 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-[box-shadow,transform,border-color] duration-300 hover:-translate-y-1 hover:border-teal-300 hover:shadow-lg"
            >
              <span className="rounded-xl bg-teal-50 p-2.5 text-brand-teal transition-colors duration-300 group-hover:bg-brand-teal group-hover:text-white">
                <MapPin className="h-4 w-4" />
              </span>
              <div>
                <p className="font-display text-base font-bold text-brand-ink">{s}</p>
                <p className="text-xs text-slate-500">
                  {page ? "House cleaning page" : `House cleaning in ${s}`}
                </p>
              </div>
              {page && <ArrowRight className="ml-auto h-4 w-4 text-brand-teal opacity-0 transition-opacity group-hover:opacity-100" />}
            </div>
          );
          return (
            <Reveal key={s} delay={0.04 * i} y={18}>
              {page ? (
                <Link to={`/${page.slug}`} data-testid={`suburb-link-${s.toLowerCase().replace(/\s+/g, "-")}`} className="block h-full">
                  {card}
                </Link>
              ) : (
                card
              )}
            </Reveal>
          );
        })}
      </div>
      <Reveal delay={0.2}>
        <p className="mt-12 max-w-2xl rounded-2xl bg-brand-mist p-6 text-sm leading-relaxed text-slate-600">
          Based in the {BUSINESS.baseSuburb} area and cleaning throughout {BUSINESS.region}. Whether it's a regular
          weekly clean in Langwarrin, an end-of-lease clean in Seaford or an ongoing commercial arrangement in
          Dandenong, we'd love to hear from you.
        </p>
      </Reveal>
    </section>
    <FinalCta />
  </>
);
